import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  HiBriefcase,
  HiChevronDown,
  HiChevronUp,
  HiCurrencyDollar,
  HiDocumentText,
  HiOutlineExternalLink,
  HiSearch,
  HiFilter,
  HiRefresh,
} from "react-icons/hi";
import TitleStripe from "../../common/TitleStripe";
import { formatDocument, formatToReal } from "../../../utils/utils";
import NoOcurrence from "../../common/query/NoOcurrence";
import { TbHammer } from "react-icons/tb";
import { useIsPrintExport } from "../../../contexts/PrintExportContext";

/* =====================================================
 * Helpers / UI tokens
 * ===================================================== */
const COLOR = {
  blue: "bg-blue-100 text-blue-700",
  green: "bg-green-100 text-green-700",
  violet: "bg-violet-100 text-violet-700",
  gray: "bg-gray-100 text-gray-600",
  amber: "bg-amber-100 text-amber-700",
  red: "bg-red-100 text-red-700",
};

const ACCENT = {
  blue: "from-blue-300 via-blue-200 to-transparent",
  green: "from-emerald-300 via-emerald-200 to-transparent",
  violet: "from-violet-300 via-violet-200 to-transparent",
  gray: "from-slate-300 via-slate-200 to-transparent",
  amber: "from-amber-300 via-amber-200 to-transparent",
  red: "from-rose-300 via-rose-200 to-transparent",
};

const CHIP = ({ children, tone = "gray", className = "" }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${COLOR[tone]} ${className}`}
  >
    {children}
  </span>
);

const ResumeBox = ({ label, value, icon: Icon, color = "blue", hint }) => (
  <div className="relative flex items-center gap-3 overflow-hidden rounded-2xl border border-gray-200 bg-white/85 p-4 shadow-sm ring-1 ring-gray-100/70 backdrop-blur transition hover:-translate-y-0.5 h-full">
    <div
      className={`absolute left-0 top-0 h-1 w-full bg-gradient-to-r ${
        ACCENT[color] || ACCENT.blue
      }`}
    />
    <div
      className={`${COLOR[color]} p-2.5 rounded-xl ring-1 ring-white/60 shadow-sm`}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex flex-col">
      <span className="text-[11px] text-gray-500 uppercase font-semibold tracking-wider">
        {label}
      </span>
      <span className="text-lg font-bold text-gray-900 mt-0.5">
        {isEmpty(value) ? "-" : value}
      </span>
      {hint ? (
        <span className="text-[11px] text-gray-400 mt-0.5">{hint}</span>
      ) : null}
    </div>
  </div>
);

const Section = ({ title, right }) => (
  <div className="flex items-center justify-between text-sm font-semibold text-gray-700 border-b pb-2">
    <span>{title}</span>
    {right}
  </div>
);

const Info = ({ label, value }) => (
  <div>
    <div className="text-[11px] text-gray-500">{label}</div>
    <div className="text-sm font-medium text-gray-900 break-words">
      {isEmpty(value) ? "-" : value}
    </div>
  </div>
);

const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isEmpty = (v) => v === undefined || v === null || v === "";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-BR");
};

const formatBool = (value) => {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  return "-";
};

const statusBadge = (status) => {
  const s = (status || "").toUpperCase();
  if (s.includes("TRAMITA")) return { tone: "green", label: status };
  if (s.includes("ARQUIV") || s.includes("ENCERR"))
    return { tone: "gray", label: status };
  if (s.includes("SUSPENS")) return { tone: "amber", label: status };
  return { tone: "blue", label: status || "Desconhecido" };
};

const usePrintMode = () => {
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mql = window.matchMedia("print");
    const onChange = (e) => setIsPrinting(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);

    const onBefore = () => setIsPrinting(true);
    const onAfter = () => setIsPrinting(false);
    window.addEventListener("beforeprint", onBefore);
    window.addEventListener("afterprint", onAfter);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
      window.removeEventListener("beforeprint", onBefore);
      window.removeEventListener("afterprint", onAfter);
    };
  }, []);

  return isPrinting;
};

/* =====================================================
 * Main Component
 * ===================================================== */
const JudicialProcesses = ({ data, isPrinting: isPrintingProp }) => {
  const isPrintingFromMedia = usePrintMode();
  const isPrintExport = useIsPrintExport();
  const effectivePrinting =
    (typeof isPrintingProp === "boolean"
      ? isPrintingProp
      : isPrintingFromMedia) || isPrintExport;
  const previousViewModeRef = useRef(null);
  const processos = data?.acoes?.processos || [];

  // ====== Derived totals ======
  const { totalProcessos, totalValor, avgValor, lastDistribuicao } =
    useMemo(() => {
      const total = processos.length;
      const soma = processos.reduce(
        (acc, p) => acc + (p?.valorCausa?.valor || 0),
        0,
      );
      const avg = total ? soma / total : 0;
      const last = processos
        .map((p) => new Date(p?.dataDistribuicao))
        .filter((d) => !Number.isNaN(d.getTime()))
        .sort((a, b) => b - a)[0];
      return {
        totalProcessos: total,
        totalValor: soma,
        avgValor: avg,
        lastDistribuicao: last ? last.toISOString() : null,
      };
    }, [processos]);

  const lesivoResumo = useMemo(
    () => ({
      qtdLesivoImagem: data?.resumoAcoes?.qtdLesivoImagem,
      temCriminalLesivoImagem: data?.resumoAcoes?.temCriminalLesivoImagem,
      qtdCriminalLesivoImagem: data?.resumoAcoes?.qtdCriminalLesivoImagem,
      qtdLesivoCredito: data?.resumoAcoes?.qtdLesivoCredito,
      qtdLesivoCreditoPassivo: data?.resumoAcoes?.qtdLesivoCreditoPassivo,
      qtdLesivoCreditoAtivo: data?.resumoAcoes?.qtdLesivoCreditoAtivo,
      valorLesivoCreditoAtivo: data?.resumoAcoes?.valorLesivoCreditoAtivo,
      valorLesivoCreditoPassivo: data?.resumoAcoes?.valorLesivoCreditoPassivo,
    }),
    [data],
  );

  // ====== Filters & sort ======
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [areaFilter, setAreaFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");
  const [limit, setLimit] = useState(10);
  const [viewMode, setViewMode] = useState("compact"); // normal | compact
  const [showAllUser, setShowAll] = useState(false);
  // No PDF (isPrintExport) força mostrar TODOS os processos, sem paginação.
  const showAll = showAllUser || effectivePrinting;

  useEffect(() => {
    if (effectivePrinting) {
      if (previousViewModeRef.current == null) {
        previousViewModeRef.current = viewMode;
      }
      if (viewMode !== "compact") setViewMode("compact");
      return;
    }

    if (previousViewModeRef.current != null) {
      setViewMode(previousViewModeRef.current);
      previousViewModeRef.current = null;
    }
  }, [effectivePrinting, viewMode]);

  const areas = useMemo(() => {
    const set = new Set();
    processos.forEach((p) => {
      const a = p?.area || p?.statusPj?.ramoDireito;
      if (!isEmpty(a)) set.add(a);
    });
    return Array.from(set);
  }, [processos]);

  const filtered = useMemo(() => {
    let arr = [...processos];

    // text query searches in number, tribunal, assuntos, classe
    const q = normalize(query);
    if (q) {
      arr = arr.filter((p) => {
        const inAssuntos = (p?.assuntosCNJ || [])
          .map((a) => normalize(a?.titulo))
          .some((t) => t.includes(q));
        return (
          normalize(p?.numeroProcessoUnico).includes(q) ||
          normalize(p?.tribunal).includes(q) ||
          normalize(p?.classeProcessual?.nome).includes(q) ||
          inAssuntos
        );
      });
    }

    if (statusFilter !== "all") {
      arr = arr.filter((p) => {
        const s = (p?.statusPj?.statusProcesso || "").toUpperCase();
        if (statusFilter === "tram") return s.includes("TRAMITA");
        if (statusFilter === "enc")
          return s.includes("ARQUIV") || s.includes("ENCERR");
        if (statusFilter === "susp") return s.includes("SUSPENS");
        return true;
      });
    }

    if (areaFilter !== "all") {
      arr = arr.filter(
        (p) => (p?.area || p?.statusPj?.ramoDireito) === areaFilter,
      );
    }

    // sort
    arr.sort((a, b) => {
      if (sortBy === "date_desc")
        return new Date(b?.dataDistribuicao) - new Date(a?.dataDistribuicao);
      if (sortBy === "date_asc")
        return new Date(a?.dataDistribuicao) - new Date(b?.dataDistribuicao);
      if (sortBy === "value_desc")
        return (b?.valorCausa?.valor || 0) - (a?.valorCausa?.valor || 0);
      if (sortBy === "value_asc")
        return (a?.valorCausa?.valor || 0) - (b?.valorCausa?.valor || 0);
      return 0;
    });

    return arr;
  }, [processos, query, statusFilter, areaFilter, sortBy]);

  const visible = showAll ? filtered : filtered.slice(0, limit);

  if (!data?.acoes?.processos)
    return (
      <section className="mt-6 space-y-6" id="acoes-processos-judiciais">
        <TitleStripe>
          <div className="flex items-center gap-2">
            <TbHammer className="text-blue-700" />{" "}
            <span>Ações e Processos Judiciais</span>
          </div>
        </TitleStripe>
        <NoOcurrence />
      </section>
    );

  return (
    <section className="mt-6 space-y-6" id="acoes-processos-judiciais">
      <div className="flex items-center justify-between">
        <TitleStripe>
          <div className="flex items-center gap-2">
            <TbHammer className="text-blue-700" />{" "}
            <span>Ações e Processos Judiciais</span>
          </div>
        </TitleStripe>
      </div>

      {/* ====== Summary Row ====== */}
      <div className="grid grid-cols-4 gap-4">
        <ResumeBox
          label="Total de Processos"
          value={totalProcessos}
          icon={HiDocumentText}
          color="gray"
        />
        <ResumeBox
          label="Valor Total das Causas"
          value={formatToReal(totalValor)}
          icon={HiCurrencyDollar}
          color="green"
        />
        <ResumeBox
          label="Valor Médio da Causa"
          value={formatToReal(avgValor)}
          icon={HiCurrencyDollar}
          color="blue"
        />
        <ResumeBox
          label="Última Distribuição"
          value={formatDate(lastDistribuicao)}
          icon={HiBriefcase}
          color="violet"
        />
      </div>

      <div className="rounded-2xl border border-indigo-200/80 bg-gradient-to-br from-indigo-50 via-white to-sky-50 p-4 sm:p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
            <HiFilter className="h-4 w-4" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-900">
              Indicadores Lesivos
            </div>
            <div className="text-[11px] text-gray-500">
              Resumo consolidado por imagem e crédito
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                Lesivo à Imagem
              </span>
              <CHIP tone="amber">Imagem</CHIP>
            </div>
            <p className="text-[11px] leading-relaxed text-amber-900/80 mb-3">
              Indicador de ocorrências judiciais que podem impactar a reputação
              pública da pessoa/empresa, com destaque para quantidade total e
              ocorrências de natureza criminal.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Quantidade</div>
                <div className="text-lg font-bold text-gray-900">
                  {isEmpty(lesivoResumo.qtdLesivoImagem)
                    ? "Nada Consta"
                    : lesivoResumo.qtdLesivoImagem}
                </div>
              </div>
              <div className="rounded-lg bg-white/80 border border-amber-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Qtd. Criminal</div>
                <div className="text-lg font-bold text-gray-900">
                  {isEmpty(lesivoResumo.qtdCriminalLesivoImagem)
                    ? "Nada Consta"
                    : lesivoResumo.qtdCriminalLesivoImagem}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-sky-200 bg-sky-50/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-800">
                Lesivo à Crédito
              </span>
              <CHIP tone="blue">Crédito</CHIP>
            </div>
            <p className="text-[11px] leading-relaxed text-sky-900/80 mb-3">
              Indicador de processos com potencial de comprometer capacidade de
              pagamento e risco financeiro, separado entre polos ativo e passivo
              e seus respectivos valores.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              <div className="rounded-lg bg-white/80 border border-sky-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Total</div>
                <div className="text-lg font-bold text-gray-900">
                  {isEmpty(lesivoResumo.qtdLesivoCredito)
                    ? "Nada Consta"
                    : lesivoResumo.qtdLesivoCredito}
                </div>
              </div>
              <div className="rounded-lg bg-white/80 border border-sky-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Ativo</div>
                <div className="text-lg font-bold text-emerald-700">
                  {isEmpty(lesivoResumo.qtdLesivoCreditoAtivo)
                    ? "-"
                    : lesivoResumo.qtdLesivoCreditoAtivo}
                </div>
              </div>
              <div className="rounded-lg bg-white/80 border border-sky-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Passivo</div>
                <div className="text-lg font-bold text-rose-700">
                  {isEmpty(lesivoResumo.qtdLesivoCreditoPassivo)
                    ? "-"
                    : lesivoResumo.qtdLesivoCreditoPassivo}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-white/80 border border-sky-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Valor Ativo</div>
                <div className="text-base font-bold text-emerald-700">
                  {isEmpty(lesivoResumo.valorLesivoCreditoAtivo)
                    ? "-"
                    : formatToReal(lesivoResumo.valorLesivoCreditoAtivo)}
                </div>
              </div>
              <div className="rounded-lg bg-white/80 border border-sky-100 px-3 py-2">
                <div className="text-[11px] text-gray-500">Valor Passivo</div>
                <div className="text-base font-bold text-rose-700">
                  {isEmpty(lesivoResumo.valorLesivoCreditoPassivo)
                    ? "-"
                    : formatToReal(lesivoResumo.valorLesivoCreditoPassivo)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== Controls ====== */}
      <div className="rounded-2xl border border-gray-200 bg-white/70 backdrop-blur p-4 flex flex-row items-center gap-3 print:hidden">
        <div className="flex-1 relative">
          <HiSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por número, tribunal, assunto ou classe..."
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 py-2 pl-8 pr-3 bg-white"
            >
              <option value="all">Status: Todos</option>
              <option value="tram">Em tramitação</option>
              <option value="susp">Suspenso</option>
              <option value="enc">Arquivado/Encerrado</option>
            </select>
            <HiFilter className="absolute left-2 top-2.5 h-5 w-5 text-gray-400" />
          </div>

          <div className="relative">
            <select
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 py-2 pl-3 pr-8 bg-white"
            >
              <option value="all">Área: Todas</option>
              {areas.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 py-2 pl-3 pr-8 bg-white"
            >
              <option value="date_desc">Mais recentes</option>
              <option value="date_asc">Mais antigos</option>
              <option value="value_desc">Maior valor</option>
              <option value="value_asc">Menor valor</option>
            </select>
          </div>

          {(query || statusFilter !== "all" || areaFilter !== "all") && (
            <button
              onClick={() => {
                setQuery("");
                setStatusFilter("all");
                setAreaFilter("all");
                setSortBy("date_desc");
              }}
              className="ml-auto text-xs inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
            >
              <HiRefresh /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* ====== Legend + View Mode ====== */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">Legenda:</span>
          <CHIP tone="green">Tramitação</CHIP>
          <CHIP tone="amber">Suspenso</CHIP>
          <CHIP tone="gray">Arquivado/Encerrado</CHIP>
          <CHIP tone="blue">Outros</CHIP>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold">Visualização:</span>
          <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white/80 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode("normal")}
              className={[
                "px-2.5 py-1 rounded-md text-[11px] font-medium transition",
                viewMode === "normal"
                  ? "bg-gray-800 text-white"
                  : "text-gray-600 hover:text-gray-800",
              ].join(" ")}
            >
              Normal
            </button>
            <button
              type="button"
              onClick={() => setViewMode("compact")}
              className={[
                "px-2.5 py-1 rounded-md text-[11px] font-medium transition",
                viewMode === "compact"
                  ? "bg-gray-800 text-white"
                  : "text-gray-600 hover:text-gray-800",
              ].join(" ")}
            >
              Compacta
            </button>
          </div>
        </div>
      </div>

      {/* ====== List ====== */}
      {visible.length === 0 ? (
        <div className="text-sm text-gray-600">Nenhum processo encontrado.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {visible.map((proc, i) => (
            <ProcessCard
              key={`${proc?.numeroProcessoUnico}-${i}`}
              processo={proc}
              compact={viewMode === "compact"}
            />
          ))}
        </div>
      )}

      {filtered.length > limit && !showAll && (
        <div className="text-center">
          <div className="inline-flex items-center gap-2">
            <button
              onClick={() => setLimit((v) => v + 10)}
              className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
            >
              Carregar mais ({filtered.length - limit} restantes)
            </button>
            <button
              onClick={() => setShowAll(true)}
              className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
            >
              Ver todos
            </button>
          </div>
        </div>
      )}

      {showAll && filtered.length > 10 && (
        <div className="text-center">
          <button
            onClick={() => setShowAll(false)}
            className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50"
          >
            Voltar a paginar
          </button>
        </div>
      )}

      {filtered.length > 10 && (
        <div className="text-[11px] text-gray-500 text-center">
          Exibindo {visible.length} de {filtered.length} resultados filtrados
          (total {totalProcessos}).
        </div>
      )}
    </section>
  );
};

/* =====================================================
 * Process Card
 * ===================================================== */
const ProcessCard = ({ processo, compact = false }) => {
  const isPrintExport = useIsPrintExport();
  const [openUser, setOpen] = useState(false);
  // No PDF, abre os detalhes de todos os processos.
  const open = openUser || isPrintExport;
  if (!processo) return null;

  const stat = statusBadge(processo?.statusPj?.statusProcesso);
  const valor = formatToReal(processo?.valorCausa?.valor);
  const area = processo?.area || processo?.statusPj?.ramoDireito;
  const classe = processo?.classeProcessual?.nome;
  const distribuicao = formatDate(processo?.dataDistribuicao);

  const detailsContent = (
    <div
      className="space-y-2 pt-4"
      onClick={compact ? (e) => e.stopPropagation() : undefined}
    >
      {/* Dados do Processo */}
      <Section title="📄 Dados do Processo" />
      <div className="grid grid-cols-3 gap-4 text-sm">
        <Info label="Órgão Julgador" value={processo?.orgaoJulgador} />
        <Info label="Sistema" value={processo?.sistema} />
        <Info label="Segmento" value={processo?.segmento} />
        <Info label="Grau" value={processo?.grauProcesso} />
        <Info
          label="Justiça Gratuita"
          value={processo?.justicaGratuita ? "Sim" : "Não"}
        />
        <Info
          label="Última Atualização"
          value={formatDate(processo?.statusPj?.dataStatus)}
        />
      </div>

      {/* Assuntos CNJ */}
      {(processo?.assuntosCNJ?.length || 0) > 0 && (
        <div className="space-y-2">
          <Section title="🧾 Assuntos CNJ" />
          <div className="flex flex-wrap gap-2">
            {processo.assuntosCNJ.map((a, i) => (
              <CHIP key={i} tone={a.principal ? "blue" : "gray"}>
                {a?.titulo}
              </CHIP>
            ))}
          </div>
        </div>
      )}

      {/* Partes */}
      <Section
        title="👥 Partes Envolvidas"
        right={
          <span className="text-[11px] text-gray-400">
            ocultando dados sensíveis quando indisponíveis
          </span>
        }
      />
      <div className="grid grid-cols-2 gap-4">
        {["ATIVO", "PASSIVO"].map((polo) => {
          const partes = (processo?.partes || []).filter(
            (p) => p.polo === polo,
          );
          if (!partes.length) return null;
          return (
            <div
              key={polo}
              className="rounded-xl border border-gray-200 bg-gray-50 p-3"
            >
              <div className="text-[11px] font-bold uppercase text-gray-500 mb-2">
                Polo {polo}
              </div>
              <div className="space-y-2">
                {partes.map((parte, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-medium text-gray-900 break-words">
                        {parte?.nome}
                      </div>
                      {parte?.tipo ? <CHIP>{parte.tipo}</CHIP> : null}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {parte?.cpf
                        ? `CPF: ${formatDocument(parte.cpf)}`
                        : parte?.cnpj
                          ? `CNPJ: ${formatDocument(parte.cnpj)}`
                          : ""}
                    </div>
                    {(parte?.advogados?.length || 0) > 0 && (
                      <div className="mt-2 text-xs text-gray-700">
                        <div className="font-semibold">Advogados</div>
                        <ul className="list-disc ml-4 mt-1 space-y-0.5">
                          {parte.advogados.map((adv, i) => (
                            <li key={i}>
                              {adv?.nome}{" "}
                              {adv?.oab?.uf && adv?.oab?.numero
                                ? `(${adv.oab.uf}-${adv.oab.numero})`
                                : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Link externo */}
      {processo?.urlProcesso && (
        <div className="text-right pt-2">
          <a
            href={processo.urlProcesso}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
          >
            Acessar processo completo <HiOutlineExternalLink />
          </a>
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <div
        className="relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <div
          className={`absolute left-0 top-0 h-full w-1 ${
            stat.tone === "green"
              ? "bg-green-400"
              : stat.tone === "amber"
                ? "bg-amber-400"
                : stat.tone === "gray"
                  ? "bg-gray-300"
                  : "bg-blue-400"
          }`}
        />
        <div className="p-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-blue-900 truncate">
                {processo?.tribunal}
              </div>
              <div className="text-[11px] text-gray-600 truncate">
                {processo?.numeroProcessoUnico}
              </div>
              {classe ? (
                <div className="text-[11px] text-gray-500 truncate">
                  {classe}
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              <CHIP tone={stat.tone} className="text-[10px]">
                {stat.label}
              </CHIP>
              {area ? (
                <CHIP tone="blue" className="text-[10px]">
                  {area}
                </CHIP>
              ) : null}
              <span
                className="ml-1 inline-flex items-center text-gray-400"
                aria-hidden="true"
                title={open ? "Fechar" : "Abrir"}
              >
                {open ? (
                  <HiChevronUp className="h-4 w-4" />
                ) : (
                  <HiChevronDown className="h-4 w-4" />
                )}
              </span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-600">
            <span>Distribuição: {distribuicao}</span>
            <span>Valor: {valor}</span>
          </div>
          {open && detailsContent}
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Accent bar */}
      <div
        className={`absolute left-0 top-0 h-full w-1 ${
          stat.tone === "green"
            ? "bg-green-400"
            : stat.tone === "amber"
              ? "bg-amber-400"
              : stat.tone === "gray"
                ? "bg-gray-300"
                : "bg-blue-400"
        }`}
      />

      <div className="p-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-blue-900 truncate">
              {processo?.tribunal}
            </div>
            <div className="text-xs text-gray-600 truncate">
              {processo?.numeroProcessoUnico}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CHIP tone={stat.tone}>{stat.label}</CHIP>
            {area ? <CHIP tone="blue">{area}</CHIP> : null}
          </div>
        </div>

        {/* Key stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <ResumeRow
            icon={HiBriefcase}
            label="Classe Processual"
            value={processo?.classeProcessual?.nome}
          />
          <ResumeRow
            icon={HiCurrencyDollar}
            label="Valor da Causa"
            value={valor}
          />
          <ResumeRow
            icon={HiDocumentText}
            label="Distribuição"
            value={formatDate(processo?.dataDistribuicao)}
          />
        </div>

        {/* Expand */}
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
          >
            {open ? "Fechar detalhes" : "Ver detalhes"}
            {open ? <HiChevronUp /> : <HiChevronDown />}
          </button>
        </div>

        {open && detailsContent}
      </div>
    </div>
  );
};

const ResumeRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
    <div className={`${COLOR.gray} p-2 rounded-lg`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900 truncate">
        {isEmpty(value) ? "-" : value}
      </div>
    </div>
  </div>
);

export default JudicialProcesses;
